import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Users, Clock, Wallet, Briefcase, Star, Loader2, Edit, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n || 0));

export default function PartnerHubDashboard() {
  const [tab, setTab] = useState("partners");
  const [partners, setPartners] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [cashflow, setCashflow] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [assignForm, setAssignForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, an, cf] = await Promise.all([
        api.get("/partner-hub/partners"),
        api.get("/partner-hub/assignments"),
        api.get("/partner-hub/analytics"),
        api.get("/partner-hub/cashflow"),
      ]);
      setPartners(p.data || []);
      setAssignments(a.data || []);
      setAnalytics(an.data);
      setCashflow(cf.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const savePartner = async () => {
    try {
      if (form.id) {
        await api.put(`/partner-hub/partners/${form.id}`, form);
        toast.success("Kumppani päivitetty");
      } else {
        await api.post("/partner-hub/partners", form);
        toast.success("Kumppani lisätty");
      }
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removePartner = async (id) => {
    if (!window.confirm("Poistetaanko kumppani?")) return;
    try {
      await api.delete(`/partner-hub/partners/${id}`);
      toast.success("Poistettu");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveAssignment = async () => {
    try {
      const p = partners.find((p) => p.id === assignForm.partner_id);
      const data = { ...assignForm, partner_name: p?.name || assignForm.partner_name };
      data.total_cost = (parseFloat(data.logged_hours) || 0) * (parseFloat(data.hourly_rate) || 0);
      if (assignForm.id) {
        await api.put(`/partner-hub/assignments/${assignForm.id}`, data);
      } else {
        await api.post("/partner-hub/assignments", data);
      }
      toast.success("Tallennettu");
      setAssignForm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = partners.filter((p) =>
    !q || p.name?.toLowerCase().includes(q.toLowerCase()) || p.email?.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className="space-y-6" data-testid="partner-hub-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Partner & Freelance Hub</p>
          <h1 className="text-3xl font-display gradient-text-luxe">Kumppanit & Freelancerit</h1>
          <p className="text-white/60 text-sm mt-1">Hallinnoi ulkoisia kumppaneita, työmääräyksiä ja tilityksiä.</p>
        </div>
        <Button onClick={() => setForm({ status: "active" })} className="btn-luxe inline-flex items-center gap-2" data-testid="add-partner">
          <Plus className="w-4 h-4" /> Lisää kumppani
        </Button>
      </header>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={<Users className="w-4 h-4" />} label="Kumppaneita" value={analytics?.partners_total ?? "—"} accent="amber" />
        <Kpi icon={<Briefcase className="w-4 h-4" />} label="Aktiiviset" value={analytics?.active_assignments ?? "—"} accent="emerald" />
        <Kpi icon={<Clock className="w-4 h-4" />} label="Kirjattuja tunteja" value={Math.round(analytics?.total_hours || 0) + "h"} accent="blue" />
        <Kpi icon={<Wallet className="w-4 h-4" />} label="Avoin maksu" value={eur(analytics?.unpaid_amount)} accent="violet" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        {[
          ["partners", "Kumppanit"],
          ["assignments", "Työmääräykset"],
          ["cashflow", "Kassavirta"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm transition ${tab === k ? "text-amber-300 border-b-2 border-amber-400" : "text-white/50 hover:text-white"}`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "partners" && (
        <>
          <div className="topbar-search flex items-center gap-2 px-3 py-2 max-w-md">
            <Search className="w-4 h-4 text-white/40" />
            <input
              type="text"
              placeholder="Hae kumppania..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="bg-transparent text-sm text-white/90 outline-none flex-1"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-white/40" />
            ) : filtered.length === 0 ? (
              <div className="text-white/40 text-sm">Ei kumppaneita</div>
            ) : (
              filtered.map((p) => (
                <div key={p.id} className="glass-card p-5" data-testid={`partner-${p.id}`}>
                  <div className="flex items-start justify-between relative z-10">
                    <div>
                      <h3 className="font-display text-base text-white">{p.name}</h3>
                      <p className="text-xs text-white/50">{p.company || "—"}</p>
                      <p className="text-xs text-white/40 mt-1">{p.email}</p>
                    </div>
                    <Badge className={`text-[10px] border-0 ${p.status === "active" ? "bg-emerald-500/20 text-emerald-200" : "bg-zinc-500/20 text-zinc-200"}`}>
                      {p.status === "active" ? "Aktiivinen" : "Pois"}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4 text-xs relative z-10">
                    <div><span className="text-white/40">Rooli:</span> <span className="text-white/80">{p.role || "—"}</span></div>
                    <div><span className="text-white/40">€/h:</span> <span className="text-amber-300">{p.hourly_rate || 0}</span></div>
                    <div><span className="text-white/40">Arvio:</span> <span className="text-amber-300 inline-flex items-center gap-1"><Star className="w-3 h-3" /> {p.rating || 0}</span></div>
                    <div><span className="text-white/40">Töitä:</span> <span className="text-white/80">{p.active_assignments || 0}</span></div>
                  </div>
                  <div className="flex gap-2 mt-4 relative z-10">
                    <Button size="sm" variant="ghost" onClick={() => setForm(p)} className="text-xs gap-1 flex-1">
                      <Edit className="w-3 h-3" /> Muokkaa
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removePartner(p.id)} className="text-xs gap-1 text-red-300">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {tab === "assignments" && (
        <div>
          <Button onClick={() => setAssignForm({ status: "pending", payment_status: "pending", hourly_rate: 0, logged_hours: 0 })} className="btn-luxe mb-4" data-testid="add-assignment">
            <Plus className="w-4 h-4 mr-1" /> Uusi työmääräys
          </Button>
          <div className="space-y-2">
            {assignments.length === 0 ? (
              <div className="text-white/40 text-sm">Ei työmääräyksiä</div>
            ) : (
              assignments.map((a) => (
                <div key={a.id} className="glass-card p-4 flex items-center justify-between gap-4">
                  <div className="relative z-10">
                    <p className="font-medium text-white text-sm">{a.project_name}</p>
                    <p className="text-xs text-white/50">{a.partner_name} · {a.logged_hours}h × {eur(a.hourly_rate)} = <span className="text-amber-300">{eur(a.total_cost)}</span></p>
                  </div>
                  <div className="flex items-center gap-2 relative z-10">
                    <Badge className={`text-[10px] border-0 ${
                      a.status === "completed" ? "bg-emerald-500/20 text-emerald-200" :
                      a.status === "active" ? "bg-blue-500/20 text-blue-200" :
                      a.status === "cancelled" ? "bg-red-500/20 text-red-200" :
                      "bg-amber-500/20 text-amber-200"
                    }`}>
                      {a.status === "completed" ? "Valmis" : a.status === "active" ? "Käynnissä" : a.status === "cancelled" ? "Peruutettu" : "Odottaa"}
                    </Badge>
                    <Badge className={`text-[10px] border-0 ${a.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-200" : "bg-zinc-500/20 text-zinc-200"}`}>
                      {a.payment_status === "paid" ? "Maksettu" : a.payment_status === "invoiced" ? "Laskutettu" : "Maksamatta"}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => setAssignForm(a)} className="text-xs"><Edit className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === "cashflow" && (
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 relative z-10 mb-4">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <p className="text-sm text-white/80">Avoimet maksut kumppaneille (lähitulevaisuus)</p>
          </div>
          <div className="space-y-2 relative z-10">
            {cashflow.length === 0 ? (
              <p className="text-white/40 text-sm">Ei avoimia tilityksiä</p>
            ) : cashflow.map((c) => (
              <div key={c.partner_name} className="flex justify-between items-center p-3 rounded-lg bg-white/[0.03] border border-white/5">
                <p className="text-sm text-white/90">{c.partner_name}</p>
                <p className="text-sm text-amber-300 font-mono">{eur(c.amount_due)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Partner form */}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Muokkaa kumppania" : "Uusi kumppani"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nimi *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Yritys</Label><Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div><Label>Rooli</Label><Input value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Esim. Asentaja" /></div>
              <div><Label>Sähköposti</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Puhelin</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>€/h</Label><Input type="number" step="0.01" value={form.hourly_rate || ""} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Y-tunnus</Label><Input value={form.tax_number || ""} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} /></div>
              <div className="col-span-2"><Label>IBAN</Label><Input value={form.iban || ""} onChange={(e) => setForm({ ...form, iban: e.target.value })} /></div>
              <div className="col-span-2 flex gap-2 pt-2">
                <Button variant="ghost" onClick={() => setForm(null)} className="flex-1">Peruuta</Button>
                <Button onClick={savePartner} className="btn-luxe flex-1" data-testid="save-partner">Tallenna</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assignment form */}
      <Dialog open={!!assignForm} onOpenChange={(v) => !v && setAssignForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{assignForm?.id ? "Muokkaa työmääräystä" : "Uusi työmääräys"}</DialogTitle></DialogHeader>
          {assignForm && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Kumppani *</Label>
                <select value={assignForm.partner_id || ""} onChange={(e) => {
                  const p = partners.find(p => p.id === e.target.value);
                  setAssignForm({ ...assignForm, partner_id: e.target.value, partner_name: p?.name || "", hourly_rate: p?.hourly_rate || 0 });
                }} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">Valitse kumppani...</option>
                  {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="col-span-2"><Label>Projektin nimi *</Label><Input value={assignForm.project_name || ""} onChange={(e) => setAssignForm({ ...assignForm, project_name: e.target.value })} /></div>
              <div><Label>€/h</Label><Input type="number" step="0.01" value={assignForm.hourly_rate} onChange={(e) => setAssignForm({ ...assignForm, hourly_rate: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Kirjatut tunnit</Label><Input type="number" step="0.5" value={assignForm.logged_hours} onChange={(e) => setAssignForm({ ...assignForm, logged_hours: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Tila</Label>
                <select value={assignForm.status} onChange={(e) => setAssignForm({ ...assignForm, status: e.target.value })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="pending">Odottaa</option><option value="active">Käynnissä</option><option value="completed">Valmis</option><option value="cancelled">Peruutettu</option>
                </select>
              </div>
              <div><Label>Maksu</Label>
                <select value={assignForm.payment_status} onChange={(e) => setAssignForm({ ...assignForm, payment_status: e.target.value })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="pending">Avoinna</option><option value="invoiced">Laskutettu</option><option value="paid">Maksettu</option>
                </select>
              </div>
              <div className="col-span-2"><Label>Kuvaus</Label><Input value={assignForm.description || ""} onChange={(e) => setAssignForm({ ...assignForm, description: e.target.value })} /></div>
              <div className="col-span-2 flex gap-2 pt-2">
                <Button variant="ghost" onClick={() => setAssignForm(null)} className="flex-1">Peruuta</Button>
                <Button onClick={saveAssignment} className="btn-luxe flex-1">Tallenna</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon, label, value, accent }) {
  const colors = {
    amber: "text-amber-300", emerald: "text-emerald-300", blue: "text-blue-300", violet: "text-violet-300",
  };
  return (
    <div className="stat-block">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center ${colors[accent]}`}>{icon}</div>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
      <p className={`text-2xl font-display font-bold mt-1 ${colors[accent]}`}>{value}</p>
    </div>
  );
}
